import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { useInView } from 'react-intersection-observer'
import gsap from 'gsap'
import delay from 'lodash.delay'

import Colors from 'styles/Colors'
import media from 'styles/media'

import { Overline, Body } from 'components/Text'
import { TextButton } from 'components/Buttons'
import { ReactComponent as BgElementSVG } from 'svg/heroBgElement.svg'
import intro from 'video/hero_v4-optimized.mp4'

import { FluidImage } from 'Types'

type Props = {
  data: {
    heroPlaceholder: FluidImage
    heroMobile: FluidImage
  }
}

const Hero: React.FC<Props> = ({ data }) => {
  const [loaded, setLoaded] = useState(false)
  const [mobile, setMobile] = useState(false)
  const [ref, inView] = useInView({ triggerOnce: false, initialInView: true })
  const [videoRef, setVideoRef] = useState(null)

  useEffect(() => {
    if (window.innerWidth > 1200 && videoRef) {
      videoRef.load()
    }
    disableBodyScroll(document.createElement('div'))
    return () => {
      clearAllBodyScrollLocks()
    }
  }, [videoRef])

  useEffect(() => {
    setMobile(window.innerWidth <= 1200)
    window.addEventListener('resize', () => {
      setMobile(window.innerWidth <= 1200)
    })
    return () => {
      window.removeEventListener('resize', () => {
        setMobile(window.innerWidth <= 1200)
      })
    }
  }, [])

  useEffect(() => {
    if (!mobile && videoRef) {
      if (inView && loaded) {
        videoRef.play()
      } else if (!inView && loaded) {
        videoRef.pause()
      }
    }
  }, [inView, loaded, mobile, videoRef])

  const handleProgress = (e) => {
    if (!loaded && !mobile) {
      if (e !== 5) {
        const text = e === 1 ? '25' : e === 2 ? '50' : e === 3 ? '75' : '99'
        gsap.to('.progress-number', {
          innerText: text,
          roundProps: 'innerText',
          duration: 0.5,
          ease: 'none',
        })
        gsap.to('.progress-bar', {
          width: `${text}%`,
          duration: 0.5,
          ease: 'none',
        })
      } else {
        gsap.to('.progress-number', {
          innerText: '100',
          roundProps: 'innerText',
          duration: 0.5,
          ease: 'none',
        })
        gsap.to('.progress-bar', { width: `100%`, duration: 0.5, ease: 'none' })
        delay(() => {
          setLoaded(true)
          clearAllBodyScrollLocks()
          videoRef.play()
        }, 750)
      }
    }
  }

  const handleImageReady = () => {
    if (!loaded && mobile) {
      gsap.to('.progress-number', {
        innerText: '100',
        roundProps: 'innerText',
        duration: 0.5,
        ease: 'none',
      })
      gsap.to('.progress-bar', {
        width: `100%`,
        duration: 0.5,
        ease: 'none',
        onComplete: () => {
          delay(() => {
            gsap.set('.hero__image', { x: 0 })
            setLoaded(true)
            clearAllBodyScrollLocks()
          }, 250)
        },
      })
    } else if (mobile) {
      gsap.set('.hero__image', { x: 0 })
    }
  }
  const Loader = () => {
    return (
      <LoaderWrapper>
        <ProgressLabel>
          <span className="progress-number">0</span>
          <span>%</span>
        </ProgressLabel>
        <ProgressWrapper>
          <Progress className="progress-bar" />
        </ProgressWrapper>
      </LoaderWrapper>
    )
  }
  const mobileImg = data.heroMobile.childImageSharp.fluid
  return (
    <>
      {!loaded && <Loader />}
      <Wrapper ref={ref}>
        {mobile ? (
          <MobileImage className="hero__image">
            <picture>
              <source
                type="image/webp"
                srcSet={mobileImg.srcSetWebp}
                sizes={mobileImg.sizes}
              />
              <source
                type="image/webp"
                srcSet={mobileImg.srcSetWebp}
                sizes={mobileImg.sizes}
              />
              <img
                src={mobileImg.src}
                srcSet={mobileImg.srcSet}
                alt=""
                width="100%"
                height="100%"
                loading="eager"
                onLoad={handleImageReady}
              />
            </picture>
          </MobileImage>
        ) : (
          <Video
            ref={(ref) => setVideoRef(ref)}
            controls={false}
            muted
            onCanPlayThrough={() => handleProgress(5)}
            onLoadStart={() => handleProgress(1)}
            onLoadedData={() => handleProgress(3)}
            onLoadedMetadata={() => handleProgress(2)}
            onCanPlay={() => handleProgress(4)}
            loop
            preload="auto"
          >
            <source src={intro} type="video/mp4" />
          </Video>
        )}
        {/* <BgElement /> */}
        <TextWrapper>
          <Label bullet>Level Up</Label>
          <Title>
            Collision-free automation. <br />
            Every time.
          </Title>
          <Paragraph>
            Engineer smarter with more efficient and productive robot
            programming from concept through deployment and into production.
          </Paragraph>
          <Link to="/products">
            <Button>Meet RapidPlan</Button>
          </Link>
        </TextWrapper>
        <Gradient1 />
        <Gradient2 />
        <Gradient3 />
        <Gradient4 />
        <Gradient5 />
        <Gradient6 />
        <Gradient7 />
      </Wrapper>
    </>
  )
}

const Wrapper = styled.section`
  height: 54.5vw;
  padding: 19.5vw 0 0 58.2vw;
  position: relative;
  background: #eceaec;
  overflow: hidden;
  @media screen and (max-width: 1080px) {
    padding: 15vw 0 0 50vw;
  }
  ${media.tabletLandscape} {
    height: 107.2vw;
    padding: 0;
    width: 100%;
    overflow: hidden;
  }
  ${media.mobile} {
    height: 127vw;
  }
  @media screen and (max-width: 600px) {
    height: 189.8vw;
    min-height: 712px;
  }
`
const Video = styled.video`
  position: absolute;
  outline: none;
  border: none;
  margin: 0;
  width: 71.1vw;
  top: 13.9vw;
  left: 8.4vw;
  z-index: 1;
  ${media.tabletLandscape} {
    display: none;
  }
`

const TextWrapper = styled.div`
  width: 384px;
  position: relative;
  z-index: 2;
  ${media.fullWidth} {
    width: 22.8vw;
  }
  ${media.tabletLandscape} {
    padding-left: 72px;
    margin-top: 48px;
    width: 428px;
    max-width: 100%;
  }
  ${media.tablet} {
    padding-left: 48px;
  }
  ${media.mobile} {
    padding-left: 12px;
  }
`
const Label = styled(Overline)``
const Title = styled.h1`
  font-weight: 500;
  font-size: 54px;
  line-height: 56px;
  margin: 15px 0 21px;
  ${media.fullWidth} {
    font-size: 3.2vw;
    line-height: 104%;
    margin: 0.9vw 0 1.2vw;
  }

  ${media.tabletLandscape} {
    width: 353px;
  }
  ${media.mobile} {
    line-height: 100%;
    font-size: 36px;
  }
`
const Paragraph = styled.p`
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 48px;
  font-family: 'Maison Neue Mono';
  ${media.fullWidth} {
    font-size: 1vw;
    line-height: 1.4vw;
    margin-bottom: 2.9vw;
  }
  ${media.tablet} {
    width: 93.6vw;
  }
`
const Button = styled(TextButton)`
  ${media.mobile} {
    width: 93.6vw;
  }
`
const Image = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  ${media.tabletLandscape} {
    display: none;
  }
`
const MobileImage = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 48.6667%;
  top: 0;
  left: 0;
  display: none;
  position: relative;
  img {
    position: absolute;
  }
  transform: translateX(-100%);
  ${media.tabletLandscape} {
    display: block;
  }
`
const BgElement = styled(BgElementSVG)`
  width: 40vw;
  position: absolute;
  top: 9.9vw;
  left: 7.5vw;
  ${media.tabletLandscape} {
    width: 466px;
    top: 0;
    left: 150px;
  }
  ${media.tablet} {
    width: 54.5vw;
    top: 0;
    left: 22.6vw;
  }
`

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100vh;
  z-index: 101010;
  position: fixed;
  top: 0;
  left: 0;
  background: ${Colors.whiteSmoke};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  // ${media.tabletLandscape} {
  //   display: none;
  // }
`
const ProgressWrapper = styled.div`
  width: 228px;
  height: 2px;
  background: ${Colors.softBlack48};
`
const Progress = styled.div`
  width: 0%;
  height: 100%;
  background: ${Colors.softBlack};
`
const ProgressLabel = styled(Body)`
  width: 228px;
  margin-bottom: 6px;
  text-align: left;
`

const Gradient1 = styled.div`
  position: absolute;
  width: 31.7vw;
  height: 24.4vw;
  left: 0;
  top: 96px;

  background: #f6f6f6;
  z-index: 0;
  ${media.tabletLandscape} {
    display: none;
  }
`
const Gradient2 = styled.div`
  position: absolute;
  width: 15.9vw;
  height: 24.4vw;
  left: 31.7vw;
  top: 96px;
  background: #f6f6f6;
  z-index: 0;
  ${media.tabletLandscape} {
    display: none;
  }
`
const Gradient3 = styled.div`
  position: absolute;
  width: 16vw;
  height: 27.4vw;
  left: 47.5vw;
  top: 96px;

  background: #f6f6f6;

  z-index: 0;

  ${media.tabletLandscape} {
    display: none;
  }
`
const Gradient4 = styled.div`
  position: absolute;
  width: 36.5vw;
  height: 27.4vw;
  left: 62vw;
  top: 96px;

  background: #f6f6f6;
  z-index: 1;
  ${media.tabletLandscape} {
    display: none;
  }
`
const Gradient5 = styled.div`
  position: absolute;
  width: 31.7vw;
  height: 24.4vw;
  left: 0;
  bottom: 0;

  background: #f6f6f6;
  z-index: 0;
  ${media.tabletLandscape} {
    display: none;
  }
`
const Gradient6 = styled.div`
  position: absolute;
  width: 31.8vw;
  height: 24.4vw;
  left: 31.7vw;
  bottom: 0;

  background: #f6f6f6;
  z-index: 0;
  ${media.tabletLandscape} {
    display: none;
  }
`
const Gradient7 = styled.div`
  position: absolute;
  width: 37vw;
  height: 23vw;
  left: 62vw;
  bottom: 0;

  background: #f6f6f6;

  z-index: 1;
  ${media.tabletLandscape} {
    display: none;
  }
`

export default Hero
